import React, { useCallback } from 'react';
import {
  HStack,
  ProductBannerCard,
  ProductBannerCardWithOverlay,
  useResponsive,
} from '@sg/garnish';

import type { DeliveryOrderDetailInput } from '@order/graphql';
import { CustomProductType } from '@order/graphql';
import { useNavigateToProductDetails } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import type { PartialProduct } from '../../../../../graphql/partials';
import { useCustomProductBannerCardContentfulData } from '../../../hooks/useCustomProductBannerCardContentfulData';

// ────────────────────────────────────────────────────────────────────────────────

export const MenuCustomCategory = (props: MenuCustomCategoryProps) => {
  const { customProducts, restaurantSlug, deliveryDetails } = props;

  const { match } = useResponsive();
  const hasMultipleProducts = customProducts.length > 1;
  const itemsPerRow = match([1, hasMultipleProducts ? 2 : 1]);

  return (
    <HStack itemsPerRow={itemsPerRow}>
      {customProducts.map((customProduct) => (
        <MenuCustomCategoryProduct
          key={customProduct.id}
          customProduct={customProduct}
          restaurantSlug={restaurantSlug}
          deliveryDetails={deliveryDetails}
        />
      ))}
    </HStack>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const MenuCustomCategoryProduct = (props: MenuCustomCategoryProductProps) => {
  const { customProduct, restaurantSlug, deliveryDetails } = props;
  const isCustomPlate = customProduct.customType === CustomProductType.Plate;
  const navigateToProduct = useNavigateToProductDetails({
    restaurantSlug,
    deliveryAddressId: deliveryDetails?.addressId,
  });
  const onCardPress = useCallback(() => {
    navigateToProduct(customProduct);
  }, [customProduct, navigateToProduct]);

  const { t } = useLocalizationContext();
  const shouldUseNewCyoBanner = useFeatureFlag('MNU-3430-short-s5-cyo-banner');
  const contentfulData = useCustomProductBannerCardContentfulData({
    customProduct,
    pause: !shouldUseNewCyoBanner,
  });

  if (shouldUseNewCyoBanner) {
    return (
      <ProductBannerCardWithOverlay
        key={customProduct.id}
        title={contentfulData.heading}
        description={contentfulData.body}
        onCtaPress={onCardPress}
        ctaLabel={contentfulData.buttonLabel}
        ctaPalette={contentfulData.buttonPalette}
        isDisabled={customProduct.outOfStock}
        backgroundImage={contentfulData.backgroundImage}
        backgroundOverlayColor={contentfulData.backgroundOverlayColor}
        textColor={contentfulData.textColor}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────

  const title = isCustomPlate
    ? t('menu.cyo.plate.title')
    : t('menu.cyo.bowl.title');
  const description = isCustomPlate
    ? t('menu.cyo.plate.description')
    : t('menu.cyo.bowl.description');
  const ctaLabel = customProduct.outOfStock
    ? t('menu.cyo.out-of-stock.cta')
    : isCustomPlate
    ? t('menu.cyo.plate.cta')
    : t('menu.cyo.bowl.cta');
  const ctaTestID = isCustomPlate ? 'menu.cyo.plate.cta' : 'menu.cyo.bowl.cta';

  // ─────────────────────────────────────────────────────────────────

  return (
    <ProductBannerCard
      key={customProduct.id}
      title={title}
      description={description}
      ctaLabel={ctaLabel}
      illustration={isCustomPlate ? 'plate' : 'bowl'}
      onCtaPress={onCardPress}
      ctaTestID={ctaTestID}
      isDisabled={customProduct.outOfStock}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type MenuCustomCategoryProps = Readonly<{
  customProducts: readonly PartialProduct[];
  restaurantSlug: string;
  deliveryDetails?: DeliveryOrderDetailInput;
}>;

type MenuCustomCategoryProductProps = Readonly<{
  customProduct: PartialProduct;
  restaurantSlug: string;
  deliveryDetails?: DeliveryOrderDetailInput;
}>;
